import type { DecimalValue } from "../../values/decimal";
import { dashboardWidget, type DashboardDateRange } from "../dashboard";
import { countNote, DASHBOARD_COPY } from "../dashboard-copy";
import { orderValue, roundMoney } from "../../orders/order-money";
import { ORDER_DIRECTIONS, type OrderDirection, type OrderStatus } from "../../orders/order-types";
import { isCancelledOrder } from "../../orders/order-views";
import { bar, countBy, coverageOf, inRange, labelOrFallback, sumBy, type AreaSummary } from "./build";
import { missingPriceNote } from "./money";

type ContactRow = { id: number; company: string; contactType: string; country: string | null };
type ContactOrder = {
  contactId: number;
  direction: OrderDirection;
  orderStatus: OrderStatus;
  quantity: DecimalValue;
  pricePerItem: number | string | null;
  submittedAt: Date | string;
  customerRequestorContactPersonId: number | null;
};

export type ContactsInput = {
  contacts: readonly ContactRow[];
  people: ReadonlyArray<{ id: number; name: string }>;
  orders: readonly ContactOrder[];
};

function countryCoverage(contacts: readonly ContactRow[]) {
  const unknown = contacts.filter((contact) => !contact.country?.trim()).length;
  return coverageOf(unknown === 0, [unknown ? countNote(DASHBOARD_COPY.notes.missingCountry, unknown) : null]);
}

function valueByCompany(orders: readonly ContactOrder[], companyOf: (id: number) => string) {
  const priced = orders.filter((order) => orderValue(order.pricePerItem, order.quantity) != null);
  const points = sumBy(priced, (order) => companyOf(order.contactId), (order) => orderValue(order.pricePerItem, order.quantity)!)
    .slice(0, dashboardWidget("orderValueByCompany").limit)
    .map((point) => ({ ...point, value: roundMoney(point.value) }));
  const missing = orders.length - priced.length;
  return bar(points, coverageOf(missing === 0, [missingPriceNote(missing)]));
}

export function summarizeContacts({ contacts, people, orders }: ContactsInput, range: DashboardDateRange): AreaSummary {
  const companies = new Map(contacts.map((contact) => [contact.id, contact.company]));
  const names = new Map(people.map((person) => [person.id, person.name]));
  const inPeriod = orders.filter((order) => !isCancelledOrder({ status: order.orderStatus }) && inRange(order.submittedAt, range));
  const sales = inPeriod.filter((order) => order.direction === ORDER_DIRECTIONS.sale);
  return {
    contactsByType: bar(countBy(contacts, (contact) => contact.contactType)),
    contactsByCountry: bar(countBy(contacts, (contact) => labelOrFallback(contact.country, DASHBOARD_COPY.unknown)), countryCoverage(contacts)),
    orderValueByCompany: valueByCompany(inPeriod, (id) => companies.get(id) ?? DASHBOARD_COPY.unknown),
    ordersByContactPerson: bar(countBy(sales, (order) => names.get(order.customerRequestorContactPersonId ?? -1) ?? DASHBOARD_COPY.unassigned)),
  };
}
